import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import { fetchCallStats, fetchAgentStats, exportReport, setPeriod } from '../store/slices/callStatsSlice';
import { FiDownload, FiRefreshCw, FiActivity, FiPieChart, FiBarChart2, FiPhone, FiClock, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, ChartTooltip, ChartLegend);

const toMinutesSeconds = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const CallStatsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { stats, agents, loading, agentsLoading, exportLoading, period, error } = useSelector((s: RootState) => s.callStats);
  const [timeRange, setTimeRangeLocal] = useState<'day' | 'week' | 'month' | 'year'>('week');

  const setTimeRange = (range: 'day' | 'week' | 'month' | 'year') => {
    setTimeRangeLocal(range);
    const p = range === 'day' ? '24h' : range === 'month' ? '30d' : range === 'year' ? '365d' : '7d';
    dispatch(setPeriod(p));
  };

  useEffect(() => {
    dispatch(fetchCallStats());
    dispatch(fetchAgentStats());
  }, [dispatch, period]);

  const handleRefresh = () => {
    dispatch(fetchCallStats());
    dispatch(fetchAgentStats());
  };

  const handleExportCSV = () => dispatch(exportReport({ format: 'csv' }));
  const handleExportPDF = () => dispatch(exportReport({ format: 'pdf' }));

  const lineData = useMemo(() => ({
    labels: stats?.series.callsPerDay.labels || [],
    datasets: [
      {
        label: 'Calls',
        data: stats?.series.callsPerDay.data || [],
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59,130,246,0.25)',
        tension: 0.3,
        fill: true,
      }
    ]
  }), [stats]);

  const barData = useMemo(() => ({
    labels: stats?.avgDurationByAgent.labels || [],
    datasets: [
      {
        label: 'Avg Duration (sec)',
        data: stats?.avgDurationByAgent.data || [],
        backgroundColor: '#8b5cf6',
      }
    ]
  }), [stats]);

  const doughnutData = useMemo(() => ({
    labels: stats?.outcomeDistribution.labels || [],
    datasets: [
      {
        data: stats?.outcomeDistribution.data || [],
        backgroundColor: ['#10b981', '#f59e0b', '#ef4444', '#6366f1', '#94a3b8'],
        borderWidth: 0
      }
    ]
  }), [stats]);

  return (
    <div className="flex-1 p-6 bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Call Statistics</h1>
              <p className="text-gray-400">Aggregated call metrics, trends, and agent performance</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={handleRefresh} className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-all flex items-center gap-2">
                <FiRefreshCw />
                {loading || agentsLoading ? 'Refreshing…' : 'Refresh'}
              </button>
              <div className="flex gap-2">
                <button onClick={handleExportCSV} disabled={exportLoading} className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2">
                  <FiDownload /> CSV
                </button>
                <button onClick={handleExportPDF} disabled={exportLoading} className="px-3 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-all flex items-center gap-2">
                  <FiDownload /> PDF
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            {(['day', 'week', 'month', 'year'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg transition-all ${timeRange === range ? 'bg-blue-500 text-white' : 'bg-slate-800 text-gray-400 hover:bg-slate-700'}`}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 text-red-300 border border-red-700">
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <button className="px-3 py-1 bg-red-600 text-white rounded" onClick={handleRefresh}>Retry</button>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-1">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400"><FiPhone /></div>
            </div>
            <h3 className="text-2xl font-bold text-white">{stats?.totals.totalCalls ?? 0}</h3>
            <p className="text-xs text-gray-400">Total Calls</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-1">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400"><FiClock /></div>
            </div>
            <h3 className="text-2xl font-bold text-white">{toMinutesSeconds(stats?.totals.avgDurationSec || 0)}</h3>
            <p className="text-xs text-gray-400">Avg Duration</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-1">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400"><FiTrendingDown /></div>
            </div>
            <h3 className="text-2xl font-bold text-white">{stats?.totals.droppedCalls ?? 0}</h3>
            <p className="text-xs text-gray-400">Dropped Calls</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-1">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400"><FiTrendingUp /></div>
            </div>
            <h3 className="text-2xl font-bold text-white">{stats?.totals.completedCalls ?? 0}</h3>
            <p className="text-xs text-gray-400">Completed Calls</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 lg:col-span-2">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiActivity className="text-blue-400" /> Calls per Day</h3>
            <div className="w-full">
              <Line data={lineData} options={{ responsive: true, plugins: { legend: { labels: { color: '#cbd5e1' } }, tooltip: { enabled: true } }, scales: { x: { ticks: { color: '#9ca3af' }, grid: { color: '#374151' } }, y: { ticks: { color: '#9ca3af' }, grid: { color: '#374151' } } } }} />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiPieChart className="text-green-400" /> Outcome Distribution</h3>
            <Doughnut data={doughnutData} options={{ plugins: { legend: { labels: { color: '#cbd5e1' } } } }} />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiBarChart2 className="text-purple-400" /> Avg Duration by Agent</h3>
            <Bar data={barData} options={{ plugins: { legend: { labels: { color: '#cbd5e1' } } }, scales: { x: { ticks: { color: '#9ca3af' }, grid: { color: '#374151' } }, y: { ticks: { color: '#9ca3af', callback: (v: any) => `${v}s` }, grid: { color: '#374151' } } } }} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Agent Performance Summary</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-gray-400">Agent Name</th>
                    <th className="text-left py-3 px-4 text-gray-400">Total Calls</th>
                    <th className="text-left py-3 px-4 text-gray-400">Avg Duration</th>
                    <th className="text-left py-3 px-4 text-gray-400">Success Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(agents || []).map((a) => (
                    <tr key={a.id} className="border-b border-slate-700/50">
                      <td className="py-3 px-4 text-white">{a.name}</td>
                      <td className="py-3 px-4 text-gray-300">{a.totalCalls}</td>
                      <td className="py-3 px-4 text-gray-300">{toMinutesSeconds(a.avgDurationSec)}</td>
                      <td className="py-3 px-4 text-gray-300">{a.successRate}%</td>
                    </tr>
                  ))}
                  {(!agents || agents.length === 0) && (
                    <tr>
                      <td className="py-4 px-4 text-gray-400" colSpan={4}>No agent data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CallStatsPage;